import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Share2, BookOpen } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { SEO } from '@/components/seo/SEO';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blog/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching blog post:', error);
      setError('Post niet gevonden');
    } finally {
      setLoading(false);
    }
  };

  const getLocalizedContent = (field) => {
    if (!post) return '';
    const localizedField = `${field}_${language}`;
    return post[localizedField] || post[`${field}_nl`] || '';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'nl' ? 'nl-NL' : language === 'de' ? 'de-DE' : language === 'fr' ? 'fr-FR' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: getLocalizedContent('title'),
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link gekopieerd!');
    }
  };

  const backText = {
    nl: 'Terug naar blog',
    en: 'Back to blog',
    fr: 'Retour au blog',
    de: 'Zurück zum Blog'
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-white pt-32">
        <div className="max-w-4xl mx-auto px-6 flex justify-center py-20">
          <div className="w-8 h-8 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
        </div>
      </main>
    );
  }

  if (error || !post) {
    return (
      <main className="min-h-screen bg-white pt-32">
        <SEO title="Artikel niet gevonden" description="Dit artikel bestaat niet of is verwijderd." noIndex={true} />
        <div className="max-w-4xl mx-auto px-6 text-center py-20">
          <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#0f172a] mb-4">Artikel niet gevonden</h1>
          <p className="text-[#475569] mb-6">Dit artikel bestaat niet of is verwijderd.</p>
          <Link
            to="/blog"
            className="btn-primary inline-flex items-center gap-2"
          >
            <ArrowLeft size={20} />
            {backText[language] || backText.nl}
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="blog-post-page">
      {/* Hero with image */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-[#f8f9fc] to-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Back link */}
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-[#475569] hover:text-[#2563EB] mb-8 transition-colors"
            >
              <ArrowLeft size={18} />
              {backText[language] || backText.nl}
            </Link>

            {/* Category & Tags */}
            <div className="flex flex-wrap gap-2 mb-4">
              {post.category && (
                <span className="text-sm font-medium text-[#2563EB] bg-[#2563EB]/10 px-3 py-1 rounded-full">
                  {post.category}
                </span>
              )}
              {post.tags?.map((tag) => (
                <span key={tag} className="text-sm text-[#475569] bg-slate-100 px-3 py-1 rounded-full">
                  #{tag}
                </span>
              ))}
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0f172a] mb-6 leading-tight">
              {getLocalizedContent('title')}
            </h1>

            {/* Meta */}
            <div className="flex items-center justify-between flex-wrap gap-4 pb-8 border-b border-slate-200">
              <div className="flex items-center gap-6 text-[#475569]">
                <span className="flex items-center gap-2">
                  <User size={18} />
                  {post.author}
                </span>
                <span className="flex items-center gap-2">
                  <Calendar size={18} />
                  {formatDate(post.published_at)}
                </span>
              </div>
              <button
                onClick={handleShare}
                className="flex items-center gap-2 text-[#475569] hover:text-[#2563EB] transition-colors"
              >
                <Share2 size={18} />
                Delen
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Image */}
      {post.image_url && (
        <section className="pb-12">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="aspect-[21/9] rounded-2xl overflow-hidden shadow-lg"
            >
              <img
                src={post.image_url}
                alt={getLocalizedContent('title')}
                decoding="async"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </section>
      )}

      {/* Content */}
      <section className="pb-20">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="prose prose-lg max-w-none"
          >
            <div 
              className="text-[#475569] leading-relaxed text-lg whitespace-pre-wrap"
              style={{ lineHeight: '1.8' }}
            >
              {getLocalizedContent('content')}
            </div>
          </motion.div>

          {/* Back to blog */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-12 pt-8 border-t border-slate-200"
          >
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-[#2563EB] font-medium hover:gap-3 transition-all"
            >
              <ArrowLeft size={18} />
              {backText[language] || backText.nl}
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default BlogPostPage;
